import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { vpsOciActions } from '@/redux/modules';
import { PLATAFORMA_ENABLE_NEW_REBUILD_SCREEN_BY_SERVER_CONFIGURED_STATUS } from '@/config/GrowthBook/constants';
import useStatusFormatter from '@/hooks/useStatusFormatter';

// Este hook é específico para VPS OCI, pois tem regras de status próprias (reboot/rebuild lockam o servidor, por exemplo). Este é mais demorado que o useServerStatus, que vai bater no whmcs.

/**
 * Self-contained hook that fetches and derives the display status for a VPS OCI server.
 *
 * @param {string|number} hostingId
 * @returns {{
 *   status: { value: string, variant: string } | null,
 *   statusData: string,
 *   loadingStatus: boolean,
 *   isRebooting: boolean,
 *   isRebuilding: boolean,
 *   isDisabledButton: boolean,
 *   isServerLocked: boolean,
 */
const useVpsStatus = (hostingId) => {
  const dispatch = useDispatch();

  const enableNewRebuildScreenByServerLockedStatus = useFeatureIsOn(
    PLATAFORMA_ENABLE_NEW_REBUILD_SCREEN_BY_SERVER_CONFIGURED_STATUS,
  );

  const serverInfo = useSelector(state => state.vpsOci?.data?.serverInfo);
  const isRebooting = useSelector(state => state.vpsOci?.isRebooting);
  const statusData = useSelector(state => state.vpsOci?.status?.value);
  const loadingStatus = useSelector(state => state.vpsOci?.status?.loading);
  const loadingRebuild = useSelector(state => state.vpsOci?.rebuild?.loading);
  const loadingChangeImage = useSelector(state => state.vpsOci?.changeImage?.loading);
  const isRebuilding = loadingRebuild || loadingChangeImage;

  const isServerLocked = serverInfo?.locked && enableNewRebuildScreenByServerLockedStatus;

  const isVpsOciOnline = useSelector(state => state.vpsOci?.status?.value === 'online');
  const isDisabledButton = isRebooting || isRebuilding || isServerLocked || !isVpsOciOnline;

  useEffect(() => {
    if (hostingId) {
      dispatch(vpsOciActions.status.get({ hostingId }));
    }
  }, [dispatch, hostingId]);

  // Determina o status atual considerando estados especiais
  let effectiveStatus = statusData;
  if (isRebooting || isRebuilding) {
    effectiveStatus = 'offline';
  } else if (isServerLocked) {
    effectiveStatus = 'rebuild';
  }

  // Usa o hook de formatação
  const status = useStatusFormatter(effectiveStatus);

  return {
    status,
    statusData,
    loadingStatus,
    isRebooting,
    isRebuilding,
    isDisabledButton,
    isServerLocked,
  };
};

export default useVpsStatus;
